import React from 'react';
import { setDeviceProperty } from '../services/api';
import DevicesMap from './DevicesMap';

const DUMMY_UUID = 'dummy-device-frontend-only';

const createDummyProps = () => ({
  name: 'Demo Microphone',
  type: 'chronos-v2',
  capsule: 'Sennheiser e845',
  recording: false,
  moveDetect: 'off',
  moved: false,
  temperature: '23.4 °C',
  humidity: '41 %',
  fallCount: 2,
  battery: { level: 78, charging: false },
  location: { latitude: 52.52, longitude: 13.405, time: new Date().toISOString() },
  network: { 0: { name: 'wlan0', ip: '192.168.1.42' }, 1: { name: 'eth0', ip: '10.0.0.5' } },
});

const DeviceList = ({ devices }) => {
  const [editingDevice, setEditingDevice] = React.useState(null);
  const [editedName, setEditedName] = React.useState('');
  const [showOnlineOnly, setShowOnlineOnly] = React.useState(true);
  const [showDummy, setShowDummy] = React.useState(false);
  const [expandedDevice, setExpandedDevice] = React.useState(null);
  const [dummyProps, setDummyProps] = React.useState(createDummyProps);
  const dummyLastSeenRef = React.useRef(Math.floor(Date.now() / 1000));

  // Frontend-only demo device for UI testing; never talks to the backend.
  const dummyDevice = React.useMemo(() => ({
    uuid: DUMMY_UUID,
    connected: true,
    shared: false,
    isDummy: true,
    lastSeen: dummyLastSeenRef.current,
    properties: dummyProps,
  }), [dummyProps]);

  // Fire a browser notification when a locked device transitions to "moved".
  // The demo device notifies directly from its test button (see handleSimulateMove).
  const prevMoveAlarmRef = React.useRef({});
  React.useEffect(() => {
    if (typeof Notification === 'undefined') return;
    const prev = prevMoveAlarmRef.current;
    const next = {};
    (devices || []).forEach((device) => {
      const moved = device.properties.moved === true || device.properties.moved === 'true' || device.properties.moved === 1 || device.properties.moved === '1';
      const alarm = device.properties.moveDetect === 'locked' && moved;
      next[device.uuid] = alarm;
      if (alarm && !prev[device.uuid] && Notification.permission === 'granted') {
        const name = device.properties.name || device.uuid;
        try {
          new Notification('⚠️ Movement detected', { body: `${name} was moved while locked.`, tag: `moved-${device.uuid}` });
        } catch { /* notifications unavailable */ }
      }
    });
    prevMoveAlarmRef.current = next;
  }, [devices]);

  const emptyStateStyle = {
    textAlign: 'center',
    padding: '48px 16px',
    color: '#9ca3af',
    fontSize: '0.9rem',
  };

  const handleRecordingToggle = async (device) => {
    const newRecordingState = (device.properties.recording === 1 || device.properties.recording === "1" || device.properties.recording === true || device.properties.recording === "true") ? false : true;
    if (device.isDummy) {
      setDummyProps(prev => ({ ...prev, recording: newRecordingState }));
      return;
    }
    try {
      await setDeviceProperty(device.uuid, "recording", newRecordingState);
    } catch (error) {
      console.error('Failed to toggle recording:', error);
    }
  };

  // Lock position monitoring: off -> lock (device captures a reference, becomes
  // "locked"); clicking while locked/armed sends "off".
  const handleMoveDetectToggle = async (device) => {
    const current = device.properties.moveDetect;
    const next = (current === 'locked' || current === 'lock') ? 'off' : 'lock';
    // Ask for notification permission on the (user-gesture) lock click.
    if (next === 'lock' && typeof Notification !== 'undefined' && Notification.permission === 'default') {
      try { Notification.requestPermission(); } catch { /* ignore */ }
    }
    if (device.isDummy) {
      // Simulate the device immediately arming into the "locked" state.
      setDummyProps(prev => ({ ...prev, moveDetect: next === 'lock' ? 'locked' : 'off', moved: false }));
      return;
    }
    try {
      await setDeviceProperty(device.uuid, "moveDetect", next);
    } catch (error) {
      console.error('Failed to toggle move detection:', error);
    }
  };

  // Frontend-only: fake a movement on the demo device to exercise the alarm UI.
  const handleSimulateMove = () => {
    setDummyProps(prev => ({ ...prev, moveDetect: 'locked', moved: true }));
    // Always surface a notification on a test, requesting permission if needed.
    if (typeof Notification === 'undefined') return;
    const fire = () => {
      try {
        // Unique tag + renotify so each click pops a fresh notification instead of collapsing.
        new Notification('⚠️ Movement detected', { body: `${dummyProps.name} was moved while locked.`, tag: `moved-${DUMMY_UUID}-${Date.now()}`, renotify: true });
      } catch { /* notifications unavailable */ }
    };
    if (Notification.permission === 'granted') {
      fire();
    } else if (Notification.permission === 'default') {
      try { Notification.requestPermission().then((p) => { if (p === 'granted') fire(); }); } catch { /* ignore */ }
    }
  };

  const handleNameClick = (device) => {
    setEditingDevice(device.uuid);
    setEditedName(device.properties.name);
  };

  const handleNameChange = (e) => {
    setEditedName(e.target.value);
  };

  const handleNameSubmit = async (device) => {
    if (editedName.trim() && editedName !== device.properties.name) {
      if (device.isDummy) {
        setDummyProps(prev => ({ ...prev, name: editedName }));
        setEditingDevice(null);
        return;
      }
      try {
        await setDeviceProperty(device.uuid, "name", editedName);
        setEditingDevice(null);
      } catch (error) {
        console.error('Failed to update device name:', error);
      }
    } else {
      setEditingDevice(null);
    }
  };

  const handleNameKeyDown = (e, device) => {
    if (e.key === 'Enter') {
      handleNameSubmit(device);
    } else if (e.key === 'Escape') {
      setEditingDevice(null);
    }
  };

  const handleCardClick = (e, uuid) => {
    // Don't expand when clicking the name (rename) or record button
    if (e.target.closest('button') || e.target.closest('input')) return;
    setExpandedDevice(prev => prev === uuid ? null : uuid);
  };

  const baseDevices = showDummy ? [dummyDevice, ...(devices || [])] : (devices || []);
  const filteredDevices = showOnlineOnly
    ? baseDevices.filter(device => device.connected)
    : baseDevices;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
        <h2 style={{ margin: 0, fontSize: '0.85rem', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.08em' }}>Devices</h2>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.82rem', color: '#6b7280', cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={showDummy}
              onChange={(e) => setShowDummy(e.target.checked)}
              style={{ cursor: 'pointer' }}
            />
            Demo device
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.82rem', color: '#6b7280', cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={showOnlineOnly}
              onChange={(e) => setShowOnlineOnly(e.target.checked)}
              style={{ cursor: 'pointer' }}
            />
            Online only
          </label>
        </div>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
        {baseDevices.length === 0 ? (
          <div style={emptyStateStyle}>
            <div style={{ fontSize: '2rem', marginBottom: '8px' }}>🎤</div>
            <div style={{ fontWeight: 600, color: '#6b7280', marginBottom: '4px' }}>No devices yet</div>
            <div>Devices you own or that are shared with you will appear here.</div>
          </div>
        ) : filteredDevices.length === 0 ? (
          <div style={emptyStateStyle}>
            No devices are online. Uncheck “Online only” to see all devices.
          </div>
        ) : filteredDevices.map((device) => {
          const isOnline = device.connected;
          const isShared = device.shared === true;
          const isRecording = device.properties.recording === 1 || device.properties.recording === "1" || device.properties.recording === true || device.properties.recording === "true";
          const isExpanded = expandedDevice === device.uuid;
          const network = device.properties.network || {};
          const location = device.properties.location;
          const locLat = (location && typeof location === 'object') ? (location.latitude) : undefined;
          const locLng = (location && typeof location === 'object') ? (location.longitude) : undefined;
          const locTime = (location && typeof location === 'object') ? location.time : undefined;
          const hasLocation = location !== undefined && location !== null &&
            (typeof location === 'string' ? location.trim() !== '' : Object.keys(location).length > 0);
          const battery = device.properties.battery || {};
          const hasBattery = battery.level !== undefined && battery.level !== null;
          const batteryLevel = hasBattery ? Number(battery.level) : null;
          const isCharging = battery.charging === true || battery.charging === 1 || battery.charging === "1";
          const batteryColor = !hasBattery ? '#9ca3af'
            : isCharging ? '#16a34a'
            : batteryLevel <= 15 ? '#ef4444'
            : batteryLevel <= 30 ? '#f59e0b'
            : '#16a34a';

          const capsule = device.properties.capsule;
          const capsuleText = capsule == null ? ''
            : (typeof capsule === 'object' ? JSON.stringify(capsule) : String(capsule));
          const hasCapsule = capsuleText.trim() !== '';

          const temperature = device.properties.temperature;
          const humidity = device.properties.humidity;
          const fallCount = device.properties.fallCount;
          const hasTemperature = temperature !== undefined && temperature !== null && String(temperature).trim() !== '';
          const hasHumidity = humidity !== undefined && humidity !== null && String(humidity).trim() !== '';
          const hasFallCount = fallCount !== undefined && fallCount !== null;
          const hasSensors = hasTemperature || hasHumidity || hasFallCount;

          const moveDetect = device.properties.moveDetect; // 'off' | 'lock' | 'locked'
          const hasMoveDetect = moveDetect !== undefined && moveDetect !== null;
          const isArming = moveDetect === 'lock';
          const isLocked = moveDetect === 'locked' || moveDetect === 'lock';
          const moved = device.properties.moved === true || device.properties.moved === 'true' || device.properties.moved === 1 || device.properties.moved === '1';
          const isMoveAlarm = moveDetect === 'locked' && moved;

          return (
            <div key={device.uuid} style={{
              backgroundColor: isMoveAlarm ? '#fef2f2' : 'white',
              border: '1px solid #e5e7eb',
              borderLeft: `3px solid ${isMoveAlarm ? '#ef4444' : isOnline ? '#d4af37' : '#e5e7eb'}`,
              borderRadius: '8px',
              boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
              overflow: 'hidden',
              cursor: 'pointer'
            }}
              onClick={(e) => handleCardClick(e, device.uuid)}
            >
              {/* Summary row */}
              <div style={{ padding: '12px 16px', display: 'flex', alignItems: 'center', gap: '12px' }}>
                {/* Status dot */}
                <div style={{
                  width: '8px', height: '8px', borderRadius: '50%',
                  backgroundColor: isOnline ? '#22c55e' : '#d1d5db',
                  flexShrink: 0,
                  boxShadow: isOnline ? '0 0 0 3px rgba(34,197,94,0.15)' : 'none'
                }} />

                {/* Device icon */}
                <span style={{ flexShrink: 0, fontSize: '1.1rem', opacity: isOnline ? 1 : 0.4 }} title="Device">🎤</span>

                {/* Name + UUID */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  {editingDevice === device.uuid ? (
                    <input
                      type="text"
                      value={editedName}
                      onChange={handleNameChange}
                      onBlur={() => handleNameSubmit(device)}
                      onKeyDown={(e) => handleNameKeyDown(e, device)}
                      autoFocus
                      style={{ fontWeight: '600', fontSize: '0.9rem', border: '1px solid #d4af37', borderRadius: '4px', padding: '2px 8px', outline: 'none' }}
                    />
                  ) : (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', minWidth: 0 }}>
                      <div
                        onClick={(e) => { e.stopPropagation(); handleNameClick(device); }}
                        style={{ fontWeight: '600', fontSize: '0.9rem', color: '#111827', cursor: 'text', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                        title={isShared ? 'Shared with you' : 'Click to rename'}
                      >
                        {device.properties.name}
                      </div>
                      {isShared && (
                        <span style={{ flexShrink: 0, fontSize: '0.66rem', fontWeight: '600', color: '#7c3aed', backgroundColor: '#f3e8ff', border: '1px solid #e9d5ff', borderRadius: '10px', padding: '1px 8px', letterSpacing: '0.03em' }} title="Shared with you">
                        🔗 Shared
                        </span>
                      )}
                    </div>
                  )}
                  <div style={{ fontSize: '0.72rem', color: '#9ca3af', fontFamily: 'monospace', marginTop: '2px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {hasCapsule ? capsuleText : device.uuid}
                  </div>
                </div>

                {/* Status + last seen */}
                <div style={{ fontSize: '0.75rem', color: '#9ca3af', flexShrink: 0, textAlign: 'right' }}>
                  <div style={{ color: isOnline ? '#16a34a' : '#9ca3af', fontWeight: '500', marginBottom: '2px' }}>
                    {isOnline ? '● Online' : '○ Offline'}
                  </div>
                  <div>{new Date(device.lastSeen * 1000).toLocaleString()}</div>
                </div>

                {/* Battery */}
                {hasBattery && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '5px', flexShrink: 0, color: batteryColor, fontSize: '0.78rem', fontWeight: '600' }} title={isCharging ? `Charging (${batteryLevel}%)` : `Battery ${batteryLevel}%`}>
                    <BatteryIcon level={batteryLevel} color={batteryColor} charging={isCharging} />
                    <span>{batteryLevel}%{isCharging ? ' ⚡' : ''}</span>
                  </div>
                )}

                {/* Lock (move detection) button */}
                {hasMoveDetect && (
                  <button
                    onClick={(e) => { e.stopPropagation(); handleMoveDetectToggle(device); }}
                    title={isMoveAlarm ? 'Movement detected! Click to disarm'
                      : isArming ? 'Arming…'
                      : isLocked ? 'Position locked - click to unlock'
                      : 'Lock position (alarm on movement)'}
                    style={{
                      flexShrink: 0,
                      backgroundColor: isMoveAlarm ? '#ef4444' : isLocked ? '#1f2937' : '#f3f4f6',
                      color: isMoveAlarm ? 'white' : isLocked ? '#fbbf24' : '#6b7280',
                      border: `1px solid ${isMoveAlarm ? '#dc2626' : isLocked ? '#374151' : '#e5e7eb'}`,
                      padding: '5px 12px',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontSize: '0.78rem',
                      fontWeight: '600',
                      letterSpacing: '0.03em',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '5px'
                    }}
                  >
                    {isMoveAlarm ? '⚠️ Moved!' : isArming ? '⏳ Arming…' : isLocked ? '🔒 Position locked' : '🔓 Lock Position'}
                  </button>
                )}

                {/* Demo-only: trigger a fake movement to test the alarm UI */}
                {device.isDummy && isLocked && (
                  <button
                    onClick={(e) => { e.stopPropagation(); handleSimulateMove(); }}
                    title="Simulate a movement on the demo device"
                    style={{
                      flexShrink: 0,
                      backgroundColor: '#fff7ed',
                      color: '#c2410c',
                      border: '1px dashed #fdba74',
                      padding: '4px 10px',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontSize: '0.72rem',
                      fontWeight: '600',
                      letterSpacing: '0.03em'
                    }}
                  >
                    Test lock
                  </button>
                )}

                {/* Record button */}
                <button
                  onClick={(e) => { e.stopPropagation(); handleRecordingToggle(device); }}
                  style={{
                    flexShrink: 0,
                    backgroundColor: isRecording ? '#ef4444' : '#f3f4f6',
                    color: isRecording ? 'white' : '#6b7280',
                    border: `1px solid ${isRecording ? '#dc2626' : '#e5e7eb'}`,
                    padding: '5px 14px',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    opacity: 1,
                    fontSize: '0.78rem',
                    fontWeight: '600',
                    letterSpacing: '0.03em',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '5px'
                  }}
                >
                  <span style={{ width: '6px', height: '6px', borderRadius: '50%', backgroundColor: isRecording ? 'rgba(255,255,255,0.9)' : '#9ca3af', display: 'inline-block' }} />
                  {isRecording ? 'Recording' : 'Not Recording'}
                </button>

                {/* Expand chevron */}
                <div style={{ color: '#9ca3af', fontSize: '0.7rem', flexShrink: 0, transition: 'transform 0.2s', transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)' }}>
                  ▼
                </div>
              </div>

              {/* Detail panel */}
              {isExpanded && (
                <div style={{ borderTop: '1px solid #f0f0f0', backgroundColor: '#fafafa', padding: '14px 16px 14px 36px', display: 'flex', gap: '32px', flexWrap: 'wrap' }}>

                  {/* Device info */}
                  <div>
                    <div style={{ fontSize: '0.7rem', fontWeight: '600', color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '8px' }}>Device</div>
                    <InfoRow label="Type" value={device.properties.type || '—'} />
                    {hasCapsule && <InfoRow label="Capsule" value={capsuleText} />}
                    <InfoRow label="UUID" value={device.uuid} mono />
                  </div>

                  {/* Location */}
                  {hasLocation && (
                    <div>
                      <div style={{ fontSize: '0.7rem', fontWeight: '600', color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '8px' }}>📍 Location</div>
                      {typeof location === 'string' ? (
                        <InfoRow label="Location" value={location} />
                      ) : (
                        <>
                          {locLat !== undefined && locLat !== null && <InfoRow label="Latitude" value={String(locLat)} mono />}
                          {locLng !== undefined && locLng !== null && <InfoRow label="Longitude" value={String(locLng)} mono />}
                          {locTime && <InfoRow label="Time" value={(() => { const d = new Date(locTime); return isNaN(d.getTime()) ? String(locTime) : d.toLocaleString(); })()} />}
                        </>
                      )}
                      {locLat !== undefined && locLat !== null && locLng !== undefined && locLng !== null && (
                        <a
                          href={`https://www.google.com/maps?q=${locLat},${locLng}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          style={{ fontSize: '0.75rem', color: '#d4af37', fontWeight: '500', textDecoration: 'none' }}
                        >
                          View on map ↗
                        </a>
                      )}
                    </div>
                  )}

                  {/* Sensors */}
                  {hasSensors && (
                    <div>
                      <div style={{ fontSize: '0.7rem', fontWeight: '600', color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '8px' }}>🌡️ Sensors</div>
                      {hasTemperature && <InfoRow label="Temperature" value={String(temperature)} />}
                      {hasHumidity && <InfoRow label="Humidity" value={String(humidity)} />}
                      {hasFallCount && <InfoRow label="Falls" value={String(fallCount)} />}
                    </div>
                  )}

                  {/* Network */}
                  {Object.keys(network).length > 0 && (
                    <div>
                      <div style={{ fontSize: '0.7rem', fontWeight: '600', color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '8px' }}>🌐 Network</div>
                      {Object.entries(network).map(([idx, iface]) => (
                        <InfoRow key={idx} label={iface.name} value={iface.ip} mono />
                      ))}
                    </div>
                  )}

                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Current device positions; follows the "Online only" filter above. */}
      <DevicesMap devices={filteredDevices} />
    </div>
  );
};

const InfoRow = ({ label, value, mono }) => (
  <div style={{ display: 'flex', gap: '10px', marginBottom: '4px', alignItems: 'baseline' }}>
    <span style={{ fontSize: '0.75rem', color: '#6b7280', minWidth: '84px', flexShrink: 0, whiteSpace: 'nowrap' }}>{label}</span>
    <span style={{ fontSize: '0.78rem', color: '#111827', fontFamily: mono ? 'monospace' : 'inherit' }}>{value}</span>
  </div>
);

const BatteryIcon = ({ level, color, charging }) => {
  const fill = Math.max(0, Math.min(100, Number(level) || 0));
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', flexShrink: 0 }}>
      <span style={{ position: 'relative', width: '22px', height: '11px', border: `1.5px solid ${color}`, borderRadius: '3px', display: 'inline-block' }}>
        <span style={{ position: 'absolute', top: '1px', left: '1px', bottom: '1px', width: `${(fill / 100) * 18}px`, backgroundColor: color, borderRadius: '1px' }} />
      </span>
      <span style={{ width: '2px', height: '5px', backgroundColor: color, borderRadius: '0 1px 1px 0', marginLeft: '1px' }} />
    </span>
  );
};

export default DeviceList;